































































import { Component, Prop, Watch, Vue } from 'vue-property-decorator';
import './Schedule.scss';
import '@/filters/dateTimeFilter';
import restService from '@/services/restService';
import vuetifyToast from 'vuetify-toast';

import { IScheduleItem } from '@/typings/timit';
import { ScheduleItemDirectionEnum } from './enums';
import Schedule from '@/models/Schedule';
import ScheduleItem from '@/models/ScheduleItem';

import DateTimePicker from '@/components/DatetimePicker.vue';
import TimePicker from '@/components/TimePicker.vue';
import { debug } from 'console';

@Component({
    components: {
      DateTimePicker,
      TimePicker,
    },
})
export default class ScheduleEditor extends Vue {
  /*tslint:disable:max-line-length*/
  private loading: boolean = false;
  
  @Prop({ required: true }) private schedule: Schedule;
  @Prop({ default: false }) private canEditSchedule: boolean;

  get canSaveSchedule() {
    return this.canEditSchedule && this.schedule.isEdited;
  }

  get disableTimePicker() {
    return !this.canEditSchedule || this.schedule.isExpired;
  }

  get showWeekends() {
    return this.$store.state.user.showWeekends;
  }

  get weekStartsOnSunday() {
    return this.$store.state.user.weekStartsOnSunday;
  }

  get headers() {
    const days = [
      { text: this.$i18n.translate('weekday.monday', 'Monday'), value: '1', align: 'center', sortable: false},
      { text: this.$i18n.translate('weekday.tuesday', 'Tuesday'), value: '2', align: 'center', sortable: false},
      { text: this.$i18n.translate('weekday.wednesday', 'Wednesday'), value: '3', align: 'center', sortable: false},
      { text: this.$i18n.translate('weekday.thursday', 'Thursday'), value: '4', align: 'center', sortable: false},
      { text: this.$i18n.translate('weekday.friday', 'Friday'), value: '5', align: 'center', sortable: false},
    ];

    if (this.showWeekends) {
      days.push({ text: this.$i18n.translate('weekday.saturday', 'Saturday'), value: '6', align: 'center', sortable: false});
      const sunday = { text: this.$i18n.translate('weekday.sunday', 'Sunday'), value: '0', align: 'center', sortable: false};
      this.weekStartsOnSunday ? days.unshift(sunday) : days.push(sunday);
    }
    return days;
  }

  get items() {
    return [ 
      this.createScheduleItemRow(ScheduleItemDirectionEnum.Start),
      this.createScheduleItemRow(ScheduleItemDirectionEnum.End)
    ];
  }

  createScheduleItemRow(direction: ScheduleItemDirectionEnum) {
    return this.headers.map(h => {
      const dayOfWeek = parseInt(h.value, 10);
      let scheduleItem = this.schedule.scheduleItems.find(si => si.direction === direction && si.dayOfWeek === dayOfWeek);
      if (!scheduleItem) {
        scheduleItem = this.createNewScheduleItem(dayOfWeek, direction);
        this.schedule.scheduleItems.push(scheduleItem);
      }
      return scheduleItem;
    });
  }

  createNewScheduleItem(dayOfWeek: number, direction: ScheduleItemDirectionEnum) {
    const scheduleItem: IScheduleItem = {
      id: 0,
      scheduleId: this.schedule.id,
      direction,
      dayOfWeek,
      time: null,
    };
    return new ScheduleItem(scheduleItem);
  }

  save() {
    const scheduleItems = this.items
      .map(row => row.filter(si => si.time))
      .reduce((items: ScheduleItem[], c) => items.concat(c), []);

    const copy = JSON.parse(JSON.stringify(this.schedule));
    copy.scheduleItems = scheduleItems;

    this.saveSchedule(copy)
  }

  async saveSchedule(schedule: Schedule) {
    this.loading = true;
    try {
      if (schedule.id === 0) {
        return await this.createSchedule(schedule);
      }
      const savedApiSchedule = await restService.schedules.updateSchedule(schedule);
      const savedSchedule = new Schedule(savedApiSchedule);
  
      this.$emit('save', savedSchedule);
      this.createSuccessToast();

    } catch (error) {
      vuetifyToast.error(this.$i18n.translate(
        'schedules.errors.saveSchedule',
        'Saving schedule failed: {message}',
        {message: (error as any).message},
      ));
    } finally {
      this.loading = false;
    }
  }

  async createSchedule(schedule: Schedule) {
    const createdApiSchedule = await restService.schedules.createSchedule(schedule);
    const createdSchedule = new Schedule(createdApiSchedule);
    this.$emit('create', createdSchedule);
    this.createSuccessToast();
  }

  createSuccessToast() {
    vuetifyToast.success(this.$i18n.translate('schedules.saveSuccess', 'Schedule was saved successfully'));
  }
  
}
